import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Key, ArrowRight, Loader2, CheckCircle, Crown, Shield, Send } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import SEO from '../../components/shared/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Magic Link Verification Page
const MagicLinkVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyMagicLink = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setError('Invalid login link');
        return;
      }

      try {
        const res = await axios.post(`${API}/auth/verify-magic-link`, { token });
        
        if (res.data.success) {
          // Store auth data
          localStorage.setItem('hive_token', res.data.token);
          localStorage.setItem('hive_user', JSON.stringify(res.data.user));
          
          setStatus('success');
          
          // Redirect after brief success message
          setTimeout(() => {
            if (res.data.user.is_first_login || !res.data.user.documents_completed) {
              // First login or incomplete documents - will show document signing
              navigate('/hive/dashboard');
            } else {
              navigate('/hive/dashboard');
            }
          }, 1500);
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Failed to verify login link');
      }
    };

    verifyMagicLink();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Verifying Your Login</h1>
            <p className="text-white/60">Please wait while we securely authenticate you...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500/10 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Welcome to the Hive</h1>
            <p className="text-white/60">Redirecting you to your dashboard...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-500/10 border-2 border-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Link Invalid or Expired</h1>
            <p className="text-white/60 mb-6">{error}</p>
            <Link 
              to="/hive/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-bold uppercase hover:bg-[#F5D47B] transition-colors"
            >
              Request New Link
              <ArrowRight className="w-5 h-5" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

// Main Passwordless Login Page
const PasswordlessLoginPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter credentials, 2: Check email
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('hive_token');
    const user = localStorage.getItem('hive_user');
    
    if (token && user) {
      const userData = JSON.parse(user);
      
      // Admin users go to Admin Command Hub
      if (userData.role === 'admin' || userData.dispatch_tier === 'admin') {
        localStorage.setItem('adminToken', 'admin-session-active');
        localStorage.setItem('adminUser', JSON.stringify(userData));
        navigate('/admin');
        return;
      }
      
      if (userData.subscription_status === 'active' || userData.subscription_status === 'trial' || 
          userData.subscription_status === 'starter_pack' || userData.subscription_status === 'pending') {
        navigate('/hive/dashboard');
      }
    }
  }, [navigate]);

  const handleRequestMagicLink = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await axios.post(`${API}/auth/request-magic-link`, {
        email: email.toLowerCase().trim(),
        access_code: accessCode.toUpperCase().trim()
      });

      if (res.data.success) {
        setMaskedEmail(res.data.email_masked);
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or access code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendLink = async () => {
    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/request-magic-link`, {
        email: email.toLowerCase().trim(),
        access_code: accessCode.toUpperCase().trim()
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="hive-login-page">
      <SEO 
        title="Enter the Hive | Queen B Logistics"
        description="Access your Queen B Logistics carrier dashboard"
      />
      
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1920&q=80)',
            filter: 'brightness(0.3)'
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <Crown className="w-10 h-10 text-[#D4AF37]" />
            <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#D4AF37] uppercase tracking-wider">
              Queen B Logistics
            </span>
          </Link>
          
          <div>
            <h1 className="font-['Barlow_Condensed'] text-5xl font-bold text-white uppercase tracking-wide mb-4">
              The Hive
            </h1>
            <p className="text-white/60 text-lg max-w-md leading-relaxed">
              Where carriers are positioned, protected, and scaled with intention. 
              Your premium freight operations command center.
            </p>
            
            <div className="mt-8 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#D4AF37]" />
                <span className="text-white/80">Build</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#D4AF37]" />
                <span className="text-white/80">Protect</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#D4AF37]" />
                <span className="text-white/80">Grow</span>
              </div>
            </div>
          </div>
          
          <p className="text-white/30 text-sm">
            Premium Freight Operations. Crowned in Confidence.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <Crown className="w-8 h-8 text-[#D4AF37]" />
              <span className="font-['Barlow_Condensed'] text-xl font-bold text-[#D4AF37] uppercase tracking-wider">
                Queen B Logistics
              </span>
            </Link>
          </div>

          {step === 1 ? (
            /* Step 1: Enter Email & Access Code */
            <div>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Enter the Hive</h1>
                <p className="text-white/60">Enter your email and access code to receive a secure login link</p>
              </div>

              <form onSubmit={handleRequestMagicLink} className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/70">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                      placeholder="you@company.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accessCode" className="text-white/70">Access Code</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <Input
                      id="accessCode"
                      type="text"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                      className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] font-mono tracking-wider"
                      placeholder="QB-XXXX-XXXX"
                      required
                      data-testid="access-code-input"
                    />
                  </div>
                  <p className="text-white/40 text-xs">Your access code was sent to you upon approval</p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="enter-hive-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending Link...
                    </>
                  ) : (
                    <>
                      Enter the Hive
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-white/40 text-sm">
                  Don't have an access code?{' '}
                  <Link to="/intake" className="text-[#D4AF37] hover:underline">
                    Apply for access
                  </Link>
                </p>
              </div>

              <div className="mt-6 text-center">
                <Link 
                  to="/admin/login"
                  className="text-white/40 hover:text-white/60 text-sm transition-colors"
                >
                  Admin Portal →
                </Link>
              </div>
            </div>
          ) : (
            /* Step 2: Check Email */
            <div className="text-center">
              <div className="w-20 h-20 bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                <Send className="w-10 h-10 text-[#D4AF37]" />
              </div>
              
              <h1 className="text-2xl font-bold text-white mb-2">Check Your Email</h1>
              <p className="text-white/60 mb-6">
                We've sent a secure login link to<br />
                <span className="text-[#D4AF37] font-medium">{maskedEmail}</span>
              </p>

              <div className="bg-[#121212] border border-white/10 p-6 mb-6">
                <div className="flex items-start gap-3 text-left">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium mb-1">Click the link in your email</p>
                    <p className="text-white/50 text-sm">
                      The link will expire in 15 minutes for your security.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-white/40 text-sm mb-4">
                Didn't receive the email? Check your spam folder or
              </p>

              <button
                onClick={handleResendLink}
                disabled={isLoading}
                className="text-[#D4AF37] hover:text-[#F5D47B] font-medium transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Sending...' : 'Resend Login Link'}
              </button>

              <div className="mt-8">
                <button
                  onClick={() => setStep(1)}
                  className="text-white/40 hover:text-white/60 text-sm transition-colors"
                >
                  ← Back to login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordlessLoginPage;
export { MagicLinkVerification };
